import urgencyOptions from '../data/selectOptions/urgency.json'

export const RULESET_METRIC_KEYS = {
    TOTAL_CPU_USAGE: 'total-cpu-usage',
    CPU_USAGE: 'cpu-usage',
    TOTAL_CPU_STEAL: 'total-cpu-steal',
    CPU_STEAL: 'cpu-steal',
    MEMORY_USAGE_PERCENTAGE: 'memory-usage-percentage',
    MEMORY_USAGE_GIB: 'memory-usage-gib',
    SWAP_USAGE_PERCENTAGE: 'swap-usage-percentage',
    SWAP_USAGE_GIB: 'swap-usage-gib',
    TOTAL_RECEIVED_BYTES_MB: 'total-received-bytes-mb',
    RECEIVED_BYTES_MB: 'received-bytes-mb',
    TOTAL_SENT_BYTES_MB: 'total-sent-bytes-mb',
    SENT_BYTES_MB: 'sent-bytes-mb',
    TOTAL_RECEIVED_PACKETS_COUNT: 'total-received-packets-count',
    RECEIVED_PACKETS_COUNT: 'received-packets-count',
    TOTAL_SENT_PACKETS_COUNT: 'total-sent-packets-count',
    SENT_PACKETS_COUNT: 'sent-packets-count',
    AVERAGE_LOAD1: 'average-load1',
    AVERAGE_LOAD5: 'average-load5',
    AVERAGE_LOAD15: 'average-load15',
    ACTIVE_USERS_COUNT: 'active-users-count',
    DISK_READ_SPEED_MBS: 'disk-read-speed-mbs',
    DISK_WRITE_SPEED_MBS: 'disk-write-speed-mbs',
    DISK_READ_BYTES_MB: 'disk-read-bytes-mb',
    DISK_WRITE_BYTES_MB: 'disk-write-bytes-mb',
    DISK_USAGE_PERCENTAGE: 'disk-usage-percentage',
    DISK_USAGE_GIB: 'disk-usage-gib'
}

const STATIC_THRESHOLD_TYPE = 'static'
const SECONDS_PER_MINUTE = 60
const AVG_VALUE_TYPE = 'avg'

export const buildMetricOptions = (rulesetMetricKeys, serverMetrics) =>
    Object.values(rulesetMetricKeys ?? {}).map(metric => {
        const categoryMeta = Object.values(serverMetrics ?? {}).find(m => m.value === metric.category) ?? {}

        return {
            ...categoryMeta,
            ...metric,
            value: metric.key,
            category_label: categoryMeta.label ?? metric.label
        }
    })

export const mapMetricRulesetsResponse = (rawRulesets = [], {metricOptions = [], conditionTypeOptions = [], valueTypeOptions = []} = {}) =>
    rawRulesets.map(raw => {
        const urgency = Object.values(urgencyOptions).find(option => option.value === raw.urgency) ?? Object.values(urgencyOptions)[0]
        const valueType = valueTypeOptions.find(option => option.value === (raw.func || 'value')) ?? valueTypeOptions[0]

        const categoryOptions = metricOptions.filter(option => option.category === raw.server_metric_type)
        const type = categoryOptions.find(option => resolveMetricType({type: option}) === raw.metric)
            ?? categoryOptions[0]
            ?? metricOptions.find(option => option.value === raw.metric)
            ?? null

        return {
            id: raw.pk ?? raw.id,
            updated_at: raw.updated_at,
            type,
            title: {value: raw.title ?? ''},
            description: {value: raw.description ?? ''},
            message: {value: raw.message ?? ''},
            urgency: {value: urgency},
            enable: {value: !!raw.enabled},
            value_type: {value: valueType},
            component: raw.extra_metric_type ? {value: raw.extra_metric_type, label: raw.extra_metric_type} : null,
            conditions: {
                pairs: (raw.conditions ?? []).map((condition, index) => ({
                    operator: {value: conditionTypeOptions.find(opt => opt.label === condition.operator) ?? null},
                    values: {value: condition.threshold != null ? String(condition.threshold) : ''},
                    ...index === 0 && {last_time: {value: Math.round((raw.window_period ?? 0) / SECONDS_PER_MINUTE)}}
                }))
            }
        }
    })

export const buildMetricBasedRulesetsPayload = (rulesets = []) =>
    rulesets.map(ruleset => {
        const [firstCondition] = ruleset.conditions?.pairs ?? []

        return {
            metric: resolveMetricType(ruleset),
            title: ruleset.title?.value,
            description: ruleset.description?.value,
            message: ruleset.message?.value,
            urgency: ruleset.urgency?.value?.value,
            enabled: !!ruleset.enable?.value,
            threshold_type: STATIC_THRESHOLD_TYPE,
            window_period: Number(firstCondition?.last_time?.value ?? 0) * SECONDS_PER_MINUTE,
            func: ruleset.value_type?.value?.value === AVG_VALUE_TYPE ? AVG_VALUE_TYPE : null,
            ...ruleset.component?.value && {extra_metric_type: ruleset.component.value},
            conditions: (ruleset.conditions?.pairs ?? [])
                .filter((pair, index) => index === 0 ||
                    (pair.values?.value !== '' && pair.values?.value !== null && pair.values?.value !== undefined))
                .map(pair => ({
                    operator: pair.operator?.value?.label,
                    threshold: Number(pair.values?.value)
                }))
        }
    })

const CPU_TOTAL_USAGE_CATEGORY = 'cpu-total-usage'

export const RULESET_METRIC_COMPONENT_SOURCE = {
    'cpu-usage': {category: CPU_TOTAL_USAGE_CATEGORY, chartKey: 'usage'},
    'cpu-steal': {category: CPU_TOTAL_USAGE_CATEGORY, chartKey: 'steal'},
    'received-bytes-mb': {category: 'network-usage-unified', chartKey: 'bytes_recv'},
    'sent-bytes-mb': {category: 'network-usage-unified', chartKey: 'bytes_sent'},
    'received-packets-count': {category: 'network-usage-unified', chartKey: 'packets_recv'},
    'sent-packets-count': {category: 'network-usage-unified', chartKey: 'packets_sent'},
    'disk-read-speed-mbs': {category: 'disk-io-unified', chartKey: 'read_speed'},
    'disk-write-speed-mbs': {category: 'disk-io-unified', chartKey: 'write_speed'},
    'disk-read-bytes-mb': {category: 'disk-io-unified', chartKey: 'read_bytes'},
    'disk-write-bytes-mb': {category: 'disk-io-unified', chartKey: 'write_bytes'},
    'disk-usage-percentage': {category: 'disk-usage-unified', chartKey: 'usage_percent'},
    'disk-usage-gib': {category: 'disk-usage-unified', chartKey: 'usage_gb'}
}
const CATEGORY_COMPONENT_CHART_KEY = Object.values(RULESET_METRIC_COMPONENT_SOURCE).reduce((acc, {category, chartKey}) => {
    if (!acc[category]) acc[category] = chartKey
    return acc
}, {})

export const categoryHasComponents = (category) => Boolean(CATEGORY_COMPONENT_CHART_KEY[category])

export const getMetricComponentOptions = (category, serverMonitoringState, serverMetrics) => {
    const chartKey = CATEGORY_COMPONENT_CHART_KEY[category]
    if (!chartKey) return []

    const groups = serverMonitoringState?.[category]?.groupedData?.[chartKey] || []
    const totalName = category === CPU_TOTAL_USAGE_CATEGORY
        ? serverMetrics?.cpu_total_usage?.charts?.[chartKey]?.total_cpu_name
        : null

    return groups
        .map(group => group.name)
        .filter(name => name && name !== totalName)
        .map(name => ({value: name, label: name}))
}

export const RULESET_METRIC_KEYS_BY_CHART = {
    [CPU_TOTAL_USAGE_CATEGORY]: {
        usage: ['total-cpu-usage', 'cpu-usage'],
        steal: ['total-cpu-steal', 'cpu-steal']
    },
    'memory-usage': {
        default: ['memory-usage-percentage'],
        total_used: ['memory-usage-gib']
    },
    'swap-usage': {
        default: ['swap-usage-percentage'],
        total_used: ['swap-usage-gib']
    },
    'network-usage-unified': {
        bytes_recv: ['total-received-bytes-mb', 'received-bytes-mb'],
        bytes_sent: ['total-sent-bytes-mb', 'sent-bytes-mb'],
        packets_recv: ['total-received-packets-count', 'received-packets-count'],
        packets_sent: ['total-sent-packets-count', 'sent-packets-count']
    },
    'system-load-unified': {
        load_average: ['average-load1', 'average-load5', 'average-load15'],
        active_users: ['active-users-count']
    },
    'disk-io-unified': {
        read_speed: ['disk-read-speed-mbs'],
        write_speed: ['disk-write-speed-mbs'],
        read_bytes: ['disk-read-bytes-mb'],
        write_bytes: ['disk-write-bytes-mb']
    },
    'disk-usage-unified': {
        usage_percent: ['disk-usage-percentage'],
        usage_gb: ['disk-usage-gib']
    }
}

export const getRelevantMetricKeys = (category, chartKey) => RULESET_METRIC_KEYS_BY_CHART[category]?.[chartKey] ?? null

const SYSTEM_LOAD_CATEGORY = 'system-load-unified'
const LOAD_WINDOW_TO_KEY = {
    15: RULESET_METRIC_KEYS.AVERAGE_LOAD15,
    5: RULESET_METRIC_KEYS.AVERAGE_LOAD5,
    1: RULESET_METRIC_KEYS.AVERAGE_LOAD1
}

const resolveLoadAverageVariant = (rowWords) => {
    for (const window of [15, 5, 1]) {
        if (rowWords.has(String(window))) return LOAD_WINDOW_TO_KEY[window]
    }
    return null
}

const STOP_WORDS = new Set(['total', 'for', 'of', 'the', 'a', 'an'])

const toLabelWordSet = (label) => new Set(
    (label || '')
        .toLowerCase().split(/[^a-z0-9]+|(?<=[a-z])(?=[0-9])|(?<=[0-9])(?=[a-z])/)
        .filter(word => word && !STOP_WORDS.has(word))
)

const findChartKeyForLabel = (charts, label) => {
    const rowWords = toLabelWordSet(label)
    if (!charts || !rowWords.size) return null

    let bestChartKey = null
    let bestScore = 0

    Object.entries(charts).forEach(([chartKey, chart]) => {
        Object.values(chart?.condition?.options ?? {}).forEach(option => {
            const chartWords = toLabelWordSet(option?.label)
            const isFullMatch = chartWords.size > 0 && [...chartWords].every(word => rowWords.has(word))

            if (isFullMatch && chartWords.size > bestScore) {
                bestScore = chartWords.size
                bestChartKey = chartKey
            }
        })
    })

    return bestChartKey
}

export const resolveMetricType = (ruleset) => {
    const category = ruleset?.type?.category
    if (!category) return category

    if (category === SYSTEM_LOAD_CATEGORY) {
        const loadVariant = resolveLoadAverageVariant(toLabelWordSet(ruleset?.type?.label))
        if (loadVariant) return loadVariant
    }

    const chartKey = findChartKeyForLabel(ruleset?.type?.charts, ruleset?.type?.label)
    const variants = getRelevantMetricKeys(category, chartKey)
    if (!variants?.length) return category

    const isComponentVariant = !!ruleset?.type?.is_setup_required
    return (isComponentVariant ? variants[1] : variants[0]) ?? variants[0]
}
